<template>
  <div class="anchor-vessel">
    <search-form
      placeholder="キーワードを入力"
      :keyword="keyword"
      :handleInput="searchAnchors"
    />
    <component
      :is="scrollbarTag"
      class="scroll-area--customizer pb-10"
      :settings="settings"
      :key="$vs.rtl"
    >
      <ul class="pb-2 mt-1">
        <li
          class="pb-2 pl-4 pr-4 mb-2"
          v-for="anchor in resultAnchors"
          :key="anchor.vesselId"
        >
          <anchor-vessel-card
            :anchor="anchor"
            :jumpToAnchor="jumpToAnchor"
            :deleteAnchorConfirm="deleteAnchorConfirm"
          ></anchor-vessel-card>
        </li>
      </ul>
    </component>
  </div>
</template>

<script>
/**
 * LiveMapの作業船位置メニューと検索フォームを描画するコンポーネント
 * @parent O.SidebarAnchor
 * @props settings: Object スクロールの設定
 * @props map: Object マップのインスタンスデータ
 */
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import SearchForm from "@/components/molecules/SearchForm";
import AnchorVesselCard from "@/components/molecules/AnchorVesselCard";
import { db } from "@/firebase/firebaseConfig";
import { ColorMixin } from "@/mixins";
import { createNamespacedHelpers, mapGetters } from "vuex";
import moment from "moment-timezone";
import { DateUtil } from "@/utils";
import Mapbox from "mapbox-gl";
import _ from "lodash";

const {
  mapMutations: mapMutationsOfLiveMap,
  mapActions: mapActionsOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
} = createNamespacedHelpers("liveMap");

const { mapState: mapStateOfAnchor, mapActions: mapActionsOfAnchor } =
  createNamespacedHelpers("gkmgAnchor");

export default {
  name: "O.AnchorVessel",
  mixins: [ColorMixin],
  components: {
    VuePerfectScrollbar,
    SearchForm,
    AnchorVesselCard,
  },
  props: {
    /** スクロールの設定 */
    settings: {
      type: Object,
      required: true,
    },
    map: Object,
  },
  data() {
    return {
      // 検索されたアンカー一覧
      searchedAnchors: [],
      // 検索ワード
      keyword: "",
      // FireStoreのリッスン解放
      unsubscribes: [],
      // 船ID・aisInfo・ポジションを格納
      vesselsInformation: [],
      // 削除対象のアンカー船
      deleteAnchorId: null,
      // ポップアップメッセージを管理
      expiredPopup: null,
      timerId: null,
      vesselDetails: [],
      vesselDetailRequestingIds: new Set(), // 船舶詳細をリクエスト中のidの集合
    };
  },
  computed: {
    ...mapGettersOfLiveMap(["getLastVesselPosition"]),
    ...mapGetters({
      vesselPositionCollection: "vesselPositionCollection",
    }),
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    ...mapStateOfAnchor({
      anchors: (state) => state.anchors,
    }),
    vesselDetailsMap() {
      if (!this.vesselDetails) return {};
      return this.vesselDetails.reduce((acc, vessel) => {
        if (vessel && vessel.id) {
          acc[vessel.id] = vessel;
        }
        return acc;
      }, {});
    },
    anchorInfo() {
      if (!this.anchors || !this.anchors.length) {
        return [];
      }
      // vessel_infoのデータと紐づけるためanchorsを整形する
      const anchors = this.anchors.map((anchor) => {
        const targetVessel = this.vesselInfoMap[anchor.vessel_id];
        const vesselDetail = this.vesselDetailsMap[anchor.vessel_id];
        const vesselNameJa = (vesselDetail && vesselDetail.name_ja) || "";
        const vesselNameEn = (vesselDetail && vesselDetail.name) || "";
        const vesselPosition =
          targetVessel && targetVessel.vesselPosition
            ? targetVessel.vesselPosition
            : null;

        return {
          ...anchor,
          vesselPosition: vesselPosition,
          // vesselName: カードに表示する船名とソート処理に使う
          vesselName: vesselNameJa || vesselNameEn || "",
          // nameJa,name: カードの検索対象ワードに使う
          nameJa: vesselNameJa,
          name: vesselNameEn,
        };
      });
      // アンカー船データを更新日時で降順にソートする
      // 更新時間が同じ場合は船名順で昇順にソートする
      return _.orderBy(
        anchors,
        [
          (anchor) => {
            // updated_atがnullかundefinedの場合は0で判定する
            return anchor.updated_at ? anchor.updated_at.seconds : 0;
          },
          (anchor) => {
            return anchor.vesselName;
          },
        ],
        ["desc", "asc"],
      );
    },
    vesselInfoMap() {
      if (!this.vesselsInformation) return {};
      return this.vesselsInformation.reduce((acc, vessel) => {
        if (vessel && vessel.vesselId) {
          acc[vessel.vesselId] = vessel;
        }
        return acc;
      }, {});
    },
    resultAnchors() {
      return this.keyword.length ? this.searchedAnchors : this.anchorInfo;
    },
  },
  methods: {
    ...mapActionsOfLiveMap(["fetchLastVesselPositions", "getVessel"]),
    ...mapActionsOfAnchor(["deleteAnchor"]),
    ...mapMutationsOfLiveMap(["REMOVE_LAST_VESSEL_POSITION"]),
    // インクリメンタルサーチ処理
    searchAnchors(nextKeyword) {
      this.keyword = nextKeyword;
      const filteredAnchorVessel = this.anchorInfo.filter((anchor) => {
        const searchTargetArray = [anchor.nameJa, anchor.name];
        return searchTargetArray.some((item) => {
          if (!item) {
            return false;
          }
          return (
            item.toUpperCase().indexOf(this.keyword.trim().toUpperCase()) !== -1
          );
        });
      });
      this.searchedAnchors = filteredAnchorVessel;
    },
    // 削除ボタンを押したときの確認ダイアログ
    deleteAnchorConfirm(vesselId) {
      this.deleteAnchorId = vesselId;
      this.$vs.dialog({
        type: "confirm",
        title: "投錨位置削除",
        text: "投錨位置を削除しますか？",
        color: "danger",
        acceptText: "削除",
        cancelText: "キャンセル",
        accept: this.deleteAnchorVessel,
      });
    },
    // 削除確認後の削除処理
    async deleteAnchorVessel() {
      await this.deleteAnchor(this.deleteAnchorId);
    },
    // 選択したアンカー船の座標に移動
    async jumpToAnchor(anchor) {
      let coordinate = null;
      if (anchor && anchor.vessel_id) {
        // lastPositionをstoreを更新
        await this.fetchLastVesselPositions(anchor.vessel_id);
        coordinate = this.getLastVesselPosition(anchor.vessel_id);
      }
      if (
        coordinate &&
        coordinate.longitude !== null &&
        coordinate.latitude !== null
      ) {
        this.map.jumpTo({
          center: [coordinate.longitude, coordinate.latitude],
        });
        this.showNotifyTimeout(coordinate);
      } else {
        this.showFailureMessage();
      }
    },
    // 最終計測時間が10分前より遅い場合はポップアップメッセージを表示する
    showNotifyTimeout(lastPosition) {
      if (!lastPosition.last_position_at) return;
      const tenMinutesAgo = moment().subtract(10, "minutes").utc().unix();
      const lastPosAt = moment(lastPosition.last_position_at).unix();

      if (lastPosAt < tenMinutesAgo) {
        const name = lastPosition.vessel_name_ja || lastPosition.vessel_name;
        const dateFormat = moment(lastPosition.last_position_at).format(
          "YYYY/MM/DD HH:mm",
        );
        let elapsed = DateUtil.elapsedTime(dateFormat);
        let popupObject = new Mapbox.Popup({
          closeOnClick: false,
          closeButton: true,
          offset: 15,
          className: "expired-vessel-live-position-popup",
        })
          .setLngLat([lastPosition.longitude, lastPosition.latitude])
          .setText(
            `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`,
          );
        this.showPopup(popupObject);
      }
    },
    // ポップアップ表示
    showPopup(popupObject) {
      if (this.expiredPopup !== null) {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      this.expiredPopup = popupObject;
      setTimeout(() => {
        this.expiredPopup.addTo(this.map);
      }, 100);
      this.timerId = setTimeout(() => {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }, 60000);
    },
    // 座標取得に失敗したときのメッセージ
    showFailureMessage() {
      this.$vs.notify({
        text: "位置情報が取得できません。",
        color: "danger",
        iconPack: "feather",
        icon: "icon-alert-circle",
        position: "top-center",
      });
    },
    // アンカー船をリッスン
    async subscribeVesselPosition(vesselId) {
      let unsubscribe = await db
        .collection(this.vesselPositionCollection)
        .doc(vesselId)
        .onSnapshot(
          (doc) => {
            if (doc.data() !== undefined) {
              for (const vessel of this.vesselsInformation) {
                if (vessel.vesselId === vesselId) {
                  vessel.vesselPosition = doc.data();
                }
              }
            }
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    // fireStoreのvessel_info(自動船舶識別装置)をリッスン
    async subscribeAisInfo(vesselId) {
      let unsubscribe = await db
        .collection("vessel_info")
        .doc(vesselId)
        .onSnapshot(
          (doc) => {
            if (doc.data() !== undefined) {
              for (const vessel of this.vesselsInformation) {
                if (vessel.vesselId === vesselId) {
                  vessel.aisInfo = doc.data();
                  this.subscribeVesselPosition(vesselId);
                }
              }
            }
          },
          function (_) {},
        );
      this.unsubscribes.push(unsubscribe);
    },
    hasVesselDetailOrRequesting(vesselId) {
      if (this.vesselDetailRequestingIds.has(vesselId)) return true;
      return this.vesselDetailsMap[vesselId] !== undefined;
    },
  },
  watch: {
    // 取得したアンカー船をリッスン開始
    anchors: {
      deep: true,
      async handler() {
        // 既にリッスン開始中の場合は処理しない
        this.anchors.forEach((anchor) => {
          if (this.vesselInfoMap[anchor.vessel_id]) return;
          this.vesselsInformation.push({
            vesselId: anchor.vessel_id,
            vesselPosition: null,
            aisInfo: null,
            onSnapshotFlag: false,
          });
        });
        for (const vessel of this.vesselsInformation) {
          if (!vessel.onSnapshotFlag) {
            vessel.onSnapshotFlag = true;
            this.subscribeAisInfo(vessel.vesselId);
          }
        }
        if (!this.anchors || !this.anchors.length) return;
        // vessel_infoのデータと紐づけるためanchorsを整形する
        await Promise.allSettled(
          this.anchors
            .filter((anchor) => anchor && anchor.vessel_id)
            .map(async (anchor) => {
              if (!this.hasVesselDetailOrRequesting(anchor.vessel_id)) {
                this.vesselDetailRequestingIds.add(anchor.vessel_id);
                this.vesselDetails.push(await this.getVessel(anchor.vessel_id));
                this.vesselDetailRequestingIds.delete(anchor.vessel_id);
              }
            }),
        );
      },
    },
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (this.unsubscribes.length !== 0) {
      this.unsubscribes.forEach((unsubscribe) => unsubscribe());
      this.unsubscribes = [];
    }
  },
};
</script>

<style lang="scss" scoped>
.anchor-vessel {
  height: 100%;

  .scroll-area--customizer {
    height: calc(100% - 10rem);
    z-index: 10;
    &:not(.ps) {
      overflow-y: auto;
    }
  }
}
</style>
